import { Copy } from "lucide-react";
import { useGetList, useInput, useTranslate } from "ra-core";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import ImageEditorField from "../misc/ImageEditorField";
import { useInboundEmail } from "../misc/useInboundEmail";
import { validateItemsInUse } from "./settingsForm";

// One component per section of the Settings page. Each renders its own title
// and inputs and nothing else, so it works wherever a settings form is around
// it: the Settings page, with its one Save for everything, or a dialog opened
// from the thing the section configures, which saves that section alone.
// See settingsSectionRegistry.ts and SettingsSectionDialog.tsx. LCCRM-86.

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h2 className="text-xl font-semibold text-muted-foreground">{children}</h2>
);

export const BrandingSection = () => {
  const translate = useTranslate();
  return (
    <div className="space-y-4">
      <SectionTitle>{translate("crm.settings.sections.branding")}</SectionTitle>
      <TextInput source="title" label="crm.settings.app_title" />
      <div className="flex gap-8">
        <div className="flex flex-col items-center gap-1">
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.light_mode_logo")}
          </p>
          <ImageEditorField
            source="lightModeLogo"
            width={100}
            height={100}
            linkPosition="bottom"
            backgroundImageColor="#f5f5f5"
          />
        </div>
        <div className="flex flex-col items-center gap-1">
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.dark_mode_logo")}
          </p>
          <ImageEditorField
            source="darkModeLogo"
            width={100}
            height={100}
            linkPosition="bottom"
            backgroundImageColor="#1a1a1a"
          />
        </div>
      </div>
    </div>
  );
};

export const InboundEmailSection = () => {
  const translate = useTranslate();
  return (
    <div className="space-y-4">
      <SectionTitle>{translate("crm.profile.inbound.title")}</SectionTitle>
      <p className="text-sm text-muted-foreground">
        {translate("crm.profile.inbound.description", {
          _: "BCC this address on an email and the CRM will save that message as a note on whoever you sent it to, creating the contact if it does not exist yet.",
          field: "BCC:",
        })}
      </p>
      <CopyPaste />
    </div>
  );
};

export const RecordTypesSection = () => {
  const translate = useTranslate();
  return (
    <div className="space-y-4">
      <SectionTitle>
        {translate("crm.settings.sections.record_types")}
      </SectionTitle>
      <p className="text-sm text-muted-foreground">
        {translate("crm.settings.record_types.help")}
      </p>
      <ArrayInput source="recordTypes" label={false} helperText={false}>
        <SimpleFormIterator disableReordering disableClear>
          <TextInput source="label" label={false} />
        </SimpleFormIterator>
      </ArrayInput>
    </div>
  );
};

export const NoteStatusesSection = () => {
  const translate = useTranslate();
  return (
    <div className="space-y-4">
      <SectionTitle>
        {translate("crm.settings.sections.note_statuses")}
      </SectionTitle>
      <ArrayInput source="noteStatuses" label={false} helperText={false}>
        <SimpleFormIterator inline disableReordering disableClear>
          <TextInput source="label" label={false} className="flex-1" />
          <ColorInput source="color" />
        </SimpleFormIterator>
      </ArrayInput>
    </div>
  );
};

const getCurrencyChoices = () => {
  const displayNames = new Intl.DisplayNames(
    typeof navigator !== "undefined"
      ? (navigator.languages as string[])
      : ["en"],
    { type: "currency" },
  );
  return Intl.supportedValuesOf("currency").map((code) => ({
    id: code,
    name: `${code} – ${displayNames.of(code)}`,
  }));
};

export const CurrencySection = () => {
  const translate = useTranslate();
  const currencyChoices = useMemo(() => getCurrencyChoices(), []);
  return (
    <div className="space-y-4">
      <SectionTitle>{translate("crm.settings.deals.currency")}</SectionTitle>
      <AutocompleteInput
        source="currency"
        label={false}
        choices={currencyChoices}
        inputText={(choice) => choice?.id}
        modal
      />
    </div>
  );
};

/**
 * Rejects removing a stage or category a deal still uses, and duplicates.
 * The two sections share one deals query, so React Query fetches it once.
 */
const useDealsInUseValidator = (
  fieldName: "stage" | "category",
  entityKey: "stages" | "categories",
) => {
  const translate = useTranslate();
  const displayName = translate(
    `crm.settings.validation.entities.${entityKey}`,
  );
  const { data: deals } = useGetList("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  return useCallback(
    (items: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(items, deals, fieldName, displayName, {
        duplicate: (name, duplicates) =>
          translate("crm.settings.validation.duplicate", {
            display_name: name,
            items: duplicates.join(", "),
          }),
        inUse: (name, inUse) =>
          translate("crm.settings.validation.in_use", {
            display_name: name,
            items: inUse.join(", "),
          }),
        validating: translate("crm.settings.validation.validating"),
      }),
    [deals, displayName, fieldName, translate],
  );
};

export const DealStagesSection = () => {
  const translate = useTranslate();
  const validateDealStages = useDealsInUseValidator("stage", "stages");
  return (
    <div className="space-y-4">
      <SectionTitle>{translate("crm.settings.deals.stages")}</SectionTitle>
      <ArrayInput
        source="dealStages"
        label={false}
        helperText={false}
        validate={validateDealStages}
      >
        <SimpleFormIterator disableClear>
          <TextInput source="label" label={false} />
        </SimpleFormIterator>
      </ArrayInput>
    </div>
  );
};

/** Reads `dealStages` from the form around it, so the form must carry them. */
export const PipelineStatusesSection = () => {
  const translate = useTranslate();
  const { watch, setValue } = useFormContext();
  const dealStages = watch("dealStages");
  const dealPipelineStatuses: string[] = watch("dealPipelineStatuses") ?? [];

  return (
    <div className="space-y-4">
      <SectionTitle>
        {translate("crm.settings.deals.pipeline_statuses")}
      </SectionTitle>
      <p className="text-sm text-muted-foreground">
        {translate("crm.settings.deals.pipeline_help")}
      </p>
      <div className="flex flex-wrap gap-2">
        {dealStages?.map(
          (stage: { value: string; label: string }, idx: number) => {
            const isSelected = dealPipelineStatuses.includes(stage.value);
            return (
              <Button
                key={idx}
                type="button"
                variant={isSelected ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  if (isSelected) {
                    setValue(
                      "dealPipelineStatuses",
                      dealPipelineStatuses.filter((s) => s !== stage.value),
                    );
                  } else {
                    setValue("dealPipelineStatuses", [
                      ...dealPipelineStatuses,
                      stage.value,
                    ]);
                  }
                }}
              >
                {stage.label || stage.value}
              </Button>
            );
          },
        )}
      </div>
    </div>
  );
};

export const DealCategoriesSection = () => {
  const translate = useTranslate();
  const validateDealCategories = useDealsInUseValidator(
    "category",
    "categories",
  );
  return (
    <div className="space-y-4">
      <SectionTitle>{translate("crm.settings.deals.categories")}</SectionTitle>
      <ArrayInput
        source="dealCategories"
        label={false}
        helperText={false}
        validate={validateDealCategories}
      >
        <SimpleFormIterator disableReordering disableClear>
          <TextInput source="label" label={false} />
        </SimpleFormIterator>
      </ArrayInput>
    </div>
  );
};

export const TaskTypesSection = () => {
  const translate = useTranslate();
  return (
    <div className="space-y-4">
      <SectionTitle>{translate("crm.settings.tasks.types")}</SectionTitle>
      <ArrayInput source="taskTypes" label={false} helperText={false}>
        <SimpleFormIterator disableReordering disableClear>
          <TextInput source="label" label={false} />
        </SimpleFormIterator>
      </ArrayInput>
    </div>
  );
};

const CopyPaste = () => {
  const translate = useTranslate();
  const inboundEmail = useInboundEmail();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    setCopied(true);
    navigator.clipboard.writeText(inboundEmail);
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            onClick={handleCopy}
            variant="ghost"
            className="normal-case justify-between w-full"
          >
            <span className="overflow-hidden text-ellipsis">
              {inboundEmail}
            </span>
            <Copy className="h-4 w-4 ml-2" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {copied
              ? translate("crm.common.copied")
              : translate("crm.common.copy")}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/** A minimal color picker input compatible with ra-core's useInput. */
const ColorInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  return (
    <input
      type="color"
      {...field}
      value={field.value || "#000000"}
      className="w-9 h-9 shrink-0 cursor-pointer appearance-none rounded border bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:cursor-pointer [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:cursor-pointer [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none"
    />
  );
};
